import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import {
  Home,
  Calendar,
  Users,
  User,
  Wrench,
  DollarSign,
  Plus,
  HelpCircle,
  Megaphone,
  Trophy,
  TrendingUp,
  Settings,
  Search,
  Bell,
  LogOut,
  Sun,
  Moon,
  Menu,
  X,
  Target,
  UserPlus,
  BarChart3,
  MapPin,
  Bug,
  ChevronDown,
  ChevronRight,
  Briefcase,
  Shield,
  Cog,
  LucideIcon,
  DollarSign as PaidMedia,
  Sparkles,
  Globe,
  Mail,
  Share2,
  PieChart,
  FileText,
  Palette,
  Code,
  Bot,
  Linkedin,
  Instagram,
  Handshake,
  Phone,
  Rocket,
  Send
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useClient } from '../contexts/ClientContext';
import ModeSelector from './ModeSelector';

interface NavItem {
  icon: LucideIcon;
  label: string;
  link?: string;
  active?: boolean;
  comingSoon?: boolean;
}

interface NavCategory {
  id: string;
  label: string;
  icon: LucideIcon;
  items: NavItem[];
  internalOnly?: boolean;
  clientOnly?: boolean;
  leadgenOnly?: boolean;
}

const navigationCategories: NavCategory[] = [
  {
    id: 'business',
    label: 'Business',
    icon: Briefcase,
    internalOnly: true,
    items: [
      { icon: Calendar, label: 'Projects', link: '/projects' },
      { icon: User, label: 'Clients', link: '/clients' },
      { icon: DollarSign, label: 'Finance', link: '/finance' },
      { icon: Mail, label: 'Email', link: '/email' },
    ]
  },
  {
    id: 'sales',
    label: 'Sales',
    icon: Handshake,
    internalOnly: true,
    items: [
      { icon: Target, label: 'Internal Leads', link: '/pipeline' },
      { icon: Mail, label: 'Contacts', link: '/contact-submissions' },
      { icon: Users, label: 'Prospects', link: '/prospects' },
      { icon: Send, label: 'Email Campaigns', link: '/email-campaigns' },
      { icon: Phone, label: 'Outreach', link: '/outreach' },
      { icon: FileText, label: 'Proposals', comingSoon: true },
    ]
  },
  {
    id: 'paid-media',
    label: 'Paid Media',
    icon: PaidMedia,
    clientOnly: true,
    items: [
      { icon: BarChart3, label: 'Google Ads', link: '/google-ads' },
      { icon: Instagram, label: 'Meta Ads', link: '/facebook-ads' },
      { icon: Linkedin, label: 'LinkedIn Ads', comingSoon: true },
    ]
  },
  {
    id: 'search-ai',
    label: 'Search & AI',
    icon: Sparkles,
    clientOnly: true,
    items: [
      { icon: Search, label: 'SEO', link: '/seo' },
      { icon: MapPin, label: 'GBP', link: '/gbp' },
      { icon: Target, label: 'Local Rankings', link: '/local-rankings' },
      { icon: Bot, label: 'GEO', comingSoon: true },
      { icon: FileText, label: 'Content', comingSoon: true },
    ]
  },
  {
    id: 'outreach',
    label: 'Outreach',
    icon: Share2,
    clientOnly: true,
    items: [
      { icon: Megaphone, label: 'Outreach', link: '/outreach' },
      { icon: Mail, label: 'Email', link: '/email' },
      { icon: Globe, label: 'Social Media', comingSoon: true },
    ]
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: PieChart,
    clientOnly: true,
    items: [
      { icon: Phone, label: 'Calls', link: '/calls' },
      { icon: Target, label: 'Leads', link: '/leads', comingSoon: true },
      { icon: TrendingUp, label: 'Reports', comingSoon: true },
    ]
  },
  {
    id: 'brand-web',
    label: 'Brand & Web',
    icon: Palette,
    clientOnly: true,
    items: [
      { icon: Globe, label: 'Landing Pages', link: '/landing-pages' },
      { icon: Code, label: 'Website', comingSoon: true },
      { icon: Palette, label: 'Brand Strategy', comingSoon: true },
    ]
  },
  {
    id: 'team',
    label: 'Team',
    icon: Users,
    internalOnly: true,
    items: [
      { icon: Users, label: 'Team', link: '/team' },
      { icon: UserPlus, label: 'Hiring', link: '/hiring' },
    ]
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: Cog,
    internalOnly: true,
    items: [
      { icon: TrendingUp, label: 'Analytics', link: '/analytics' },
      { icon: HelpCircle, label: 'Requests', link: '/requests' },
      { icon: Wrench, label: 'Services', link: '/services' },
    ]
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: Shield,
    internalOnly: true,
    items: [
      { icon: Bug, label: 'Tracking', link: '/tracking' },
      { icon: Trophy, label: 'Quality' },
      { icon: Settings, label: 'Settings' },
    ]
  },
  {
    id: 'leadgen-overview',
    label: 'Lead Generation',
    icon: Rocket,
    leadgenOnly: true,
    items: [
      { icon: BarChart3, label: 'Dashboard', link: '/leadgen' },
      { icon: Users, label: 'Leads', link: '/leadgen/leads' },
    ]
  },
];

const Layout: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { mode } = useClient();

  const filteredCategories = navigationCategories.filter(cat => {
    if (mode === 'internal') return !cat.clientOnly && !cat.leadgenOnly;
    if (mode === 'client') return !cat.internalOnly && !cat.leadgenOnly;
    if (mode === 'leadgen') return !cat.internalOnly && !cat.clientOnly;
    return true;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([
    'business', 'sales', 'paid-media', 'search-ai', 'outreach'
  ]);
  const location = useLocation();

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const isItemActive = (item: NavItem) => {
    return item.link ? location.pathname === item.link : item.active;
  };

  const isCategoryActive = (category: NavCategory) => {
    return category.items.some(item => isItemActive(item));
  };

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-950">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Compact */}
      <div className={`fixed lg:static inset-y-0 left-0 z-50 w-56 bg-white dark:bg-gray-900 shadow-sm border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-200 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="p-2 h-full overflow-y-auto">
          <div className="flex items-center justify-center mb-3">
            <div className="w-36 h-14 flex items-center justify-center">
              <img
                src={theme === 'light' ? '/logo_light.png' : '/logo_dark.png'}
                alt="Logo"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          <nav className="space-y-1">
            {/* Home */}
            <Link
              to="/"
              className={`flex items-center gap-2 px-2 py-1 rounded text-sm font-medium transition-colors ${
                location.pathname === '/'
                  ? 'bg-primary-100/80 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-medium'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-elevated'
              }`}
            >
              <Home className="w-5 h-5" />
              <span className="font-medium">Home</span>
            </Link>

            {/* Category Groups */}
            {filteredCategories.map((category) => {
              const isExpanded = expandedCategories.includes(category.id);
              const hasActiveItem = isCategoryActive(category);

              return (
                <div key={category.id} className="mt-1">
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className={`w-full flex items-center justify-between px-2 py-1 rounded text-sm font-medium transition-colors ${
                      hasActiveItem
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <category.icon className="w-5 h-5" />
                      <span className="font-semibold">{category.label}</span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>

                  {/* Category Items */}
                  {isExpanded && (
                    <div className="ml-3 mt-1 space-y-1 border-l border-gray-200 dark:border-gray-700 pl-2">
                      {category.items.map((item) => {
                        const isActive = isItemActive(item);
                        const content = (
                          <>
                            <item.icon className={`w-5 h-5 ${item.comingSoon ? 'opacity-40' : ''}`} />
                            <span className="text-sm">{item.label}</span>
                            {item.comingSoon && (
                              <span className="ml-auto text-sm px-1 py-1 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded">
                                Soon
                              </span>
                            )}
                          </>
                        );

                        if (item.comingSoon) {
                          return (
                            <div
                              key={item.label}
                              className="flex items-center gap-2 px-2 py-1 rounded text-gray-400 dark:text-gray-500 cursor-not-allowed"
                            >
                              {content}
                            </div>
                          );
                        }

                        return item.link ? (
                          <Link
                            key={item.label}
                            to={item.link}
                            className={`flex items-center gap-2 px-2 py-1 rounded transition-colors ${
                              isActive
                                ? 'bg-primary-500 text-black font-medium'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                            }`}
                          >
                            {content}
                          </Link>
                        ) : (
                          <button
                            key={item.label}
                            className={`w-full flex items-center gap-2 px-2 py-1 rounded transition-colors ${
                              isActive
                                ? 'bg-primary-500 text-black font-medium'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                            }`}
                          >
                            {content}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
          <div className="mt-4 pt-2 border-t border-gray-200 dark:border-gray-700 text-center">
            <span className="text-sm text-gray-400">{process.env.REACT_APP_BUILD_VERSION || 'dev'}</span>
          </div>
        </div>

        {/* Mobile Close Button */}
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden absolute top-2 right-2 p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header - Compact */}
        <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-3 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
              <div className="hidden sm:block">
                <ModeSelector />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={toggleTheme}
                className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                {theme === 'light' ? (
                  <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                ) : (
                  <Sun className="w-5 h-5 text-gray-300" />
                )}
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300 font-semibold hidden sm:block">
                {user?.first_name || user?.username || 'User'}
              </span>
              <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center text-black font-semibold text-sm">
                {user?.first_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
              </div>
              <button className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <Bell className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              </button>
              <button
                onClick={logout}
                className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title="Logout"
              >
                <LogOut className="w-5 h-5 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400" />
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {/* Floating Action Button - Compact */}
      <button className="fixed bottom-3 right-3 lg:bottom-4 lg:right-4 w-10 h-10 bg-primary-500 text-black rounded-full shadow-lg hover:bg-primary-600 transition-colors flex items-center justify-center">
        <Plus className="w-5 h-5" />
      </button>
    </div>
  );
};

export default Layout;
